import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ColorPicker, useColor, ColorService } from "react-color-palette";
import "react-color-palette/css";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import Select from "@/components/ui/select";
import FontSelect from "@/components/ui/font-select";
import Toggle from "@/components/ui/toggle";
import IconArrowLeftLane from "@/components/icons/icon-arrow-left-lane";
import IconTemplate from "@/components/icons/icon-template";
import IconChatBubbleSquare from "@/components/icons/icon-chat-bubble-square";
import { useDirtyState } from "@/modules/form/components/form-builder/hooks/use-dirty-state";
import { useNavigationGuard } from "@/contexts/navigation-guard-context";
import { useFonts } from "@/modules/brand/hooks/use-fonts";
import type { Tag } from "@/modules/tag/types";
import type { BrandKit } from "@/modules/brand/types";
import type { UserRole } from "@/modules/membership";
import type { Review, ReviewFilters, ReviewRatingFilter, ReviewSource, ReviewType, ReviewContentType } from "@/modules/review/types";
import { getMockReviews } from "@/modules/review/constants/mock-reviews";
import { useWidgets } from "../hooks/use-widgets";
import { WidgetPlanLimitError } from "../stores/widget-store";
import type {
	Widget,
	WidgetAggregationConfig,
	WidgetPreviewAggregation,
	WidgetPreviewResponse,
	WidgetTemplateDetail,
	WidgetTemplateListItem,
	WidgetDisplayType,
	WidgetType,
	WidgetUpdateRequest,
	WidgetPreviewRequest,
	WidgetSchemaField,
} from "../types";
import WidgetTemplateModal from "./widget-template-modal";
import WidgetTemplateSelector from "./widget-template-selector";
import EmbedCodeModal from "./embed-code-modal";
import StatusChangeConfirmationModal from "./status-change-confirmation-modal";
import UpgradeToGrowthModal from "@/components/ui/upgrade-to-growth-modal";
import IconCodeMonitor from "@/components/icons/icon-code-monitor";
import Tooltip from "@/components/ui/tooltip";
import { WidgetTemplateRouter, loadGoogleFont, resolveWeights } from "@/modules/widget-template";
import {
	ReviewPinnedSelectionSummary,
	ReviewSelectionSummary,
	StaticReviewSelectionSummary,
	SelectPinnedReviewsModal,
	SelectReviewsModal,
	SelectSingleReviewModal,
	SelectStaticReviewsModal,
	SelectAggregationFiltersModal,
} from "@/modules/review-selection";
import type { ReviewSelectionFilters, ReviewSelectionSort } from "@/modules/review-selection/types";
import { usePlan } from "@/modules/brand";

const PREVIEW_DEBOUNCE_MS = 400;

const DEFAULT_DYNAMIC_FILTERS: ReviewSelectionFilters = {
	logic: "and",
	groups: {},
};

type TabType = "content" | "design";

export interface WidgetSaveContext {
	widgetType: WidgetDisplayType;
	widgetMode: WidgetType;
	selectedReviewIds: string[];
	pinnedReviewIds: string[];
	selectedReviewsChanged: boolean;
	pinnedReviewsChanged: boolean;
}

interface WidgetBuilderProps {
	brandUrl: string;
	tags: Tag[] | null;
	widgetData: Widget | null;
	pinnedReviewIds: string[];
	curatedReviewIds: string[];
	workspaceId: string;
	brandKit: BrandKit | null;
	showBranding?: boolean;
	maxReviewsPerWidget?: number;
	allowDynamicWidgets?: boolean;
	userRole?: string;
	onSave: (payload: WidgetUpdateRequest, context: WidgetSaveContext) => Promise<{ success: boolean; validationErrors?: Record<string, string[]> }>;
	justCreated?: boolean;
}

interface BuilderState {
	title: string;
	type: WidgetType;
	display_type: WidgetDisplayType;
	template: string;
	template_version: number;
	content_config: Record<string, unknown>;
	layout_config: Record<string, unknown>;
	filters: ReviewSelectionFilters | null;
	sort: ReviewSelectionSort | null;
	aggregation_config: WidgetAggregationConfig | null;
	reviewIds: string[];
	pinnedReviewIds: string[];
	status: "published" | "unpublished";
	reviewCounts: {
		total: number;
		pinned: number;
		curated: number;
		unpinned: number;
	};
}

function convertSelectionFiltersToReviewFilters(filters: ReviewSelectionFilters | null): ReviewFilters {
	const reviewFilters: ReviewFilters = {};

	if (!filters?.groups) return reviewFilters;

	if (filters.groups.rating) {
		const { value, operator } = filters.groups.rating;
		if (operator === "gte" && typeof value === "number") {
			const ratingMap: Record<number, ReviewRatingFilter> = {
				5: "5_and_above",
				4: "4_and_above",
				3: "3_and_above",
				2: "2_and_above",
				1: "1_and_above",
			};
			if (ratingMap[value]) {
				reviewFilters.rating = ratingMap[value];
			}
		}
	}

	if (filters.groups.type) {
		const { value, operator } = filters.groups.type;
		if (operator === "eq" && typeof value === "string") {
			reviewFilters.type = value as ReviewType;
		}
	}

	if (filters.groups.source) {
		const { value, operator } = filters.groups.source;
		if ((operator === "in" || operator === "eq") && typeof value === "string") {
			const sources = value.split(",").map((s) => s.trim()) as ReviewSource[];
			reviewFilters.source = sources.length === 1 ? sources[0] : sources;
		}
	}

	if (filters.groups.content_type) {
		const { value, operator } = filters.groups.content_type;
		if ((operator === "in" || operator === "eq") && typeof value === "string") {
			const types = value.split(",").map((s) => s.trim()) as ReviewContentType[];
			reviewFilters.content_type = types.length === 1 ? types[0] : types;
		}
	}

	if (filters.groups.date) {
		const { value, operator } = filters.groups.date;
		if (typeof value === "string") {
			reviewFilters.date = `${operator}:${value}`;
		}
	}

	if (filters.groups.tags) {
		const { value } = filters.groups.tags;
		if (Array.isArray(value)) {
			reviewFilters.tags = value;
		} else if (typeof value === "string") {
			reviewFilters.tags = value.split(",").map((s) => s.trim());
		}
	}

	return reviewFilters;
}

function SingleReviewSelectionSummary({ review, onClick, disabled = false }: { review: Review | null; onClick: () => void; disabled?: boolean }) {
	const avatarGradient = review ? `hsl(${((review.customer_name || "A").charCodeAt(0) * 137) % 360}, 70%, 60%)` : "";

	return (
		<button
			className={`w-full p-4 rounded-xl border text-left transition-all ${disabled ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"}`}
			onClick={onClick}
			disabled={disabled}
		>
			{review ? (
				<div className="flex items-start gap-3">
					{/* Avatar */}
					<div className="shrink-0">
						{review.customer_avatar_url ? (
							<img src={review.customer_avatar_url} alt={review.customer_name} className="w-10 h-10 rounded-full object-cover" />
						) : (
							<div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium" style={{ backgroundColor: avatarGradient }}>
								{(review.customer_name || "A").charAt(0).toUpperCase()}
							</div>
						)}
					</div>
					{/* Info */}
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-2 mb-0.5">
							<span className="text-sm font-medium text-gray-900 truncate">{review.customer_name || "Anonymous"}</span>
							{review.rating > 0 && (
								<div className="flex items-center gap-0.5 shrink-0">
									{[1, 2, 3, 4, 5].map((star) => (
										<svg key={star} className={`w-3 h-3 ${star <= review.rating ? "text-amber-400" : "text-gray-200"}`} fill="currentColor" viewBox="0 0 20 20">
											<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
										</svg>
									))}
								</div>
							)}
						</div>
						{review.content_text && <p className="text-sm text-tertiary line-clamp-2">{review.content_text}</p>}
						{review.customer_job_title && review.customer_company_name && (
							<p className="text-xs text-tertiary mt-1">
								{review.customer_job_title} at {review.customer_company_name}
							</p>
						)}
					</div>
					{/* Change action */}
					<span className="text-sm font-medium text-gray-600 flex items-center gap-1 shrink-0 mt-0.5">
						Change
						<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
						</svg>
					</span>
				</div>
			) : (
				<>
					<div className="flex items-center gap-2 mb-2">
						<span className="text-sm font-medium text-gray-900">No review selected</span>
					</div>
					<div className="flex items-center justify-between">
						<span className="text-sm text-tertiary truncate pr-2">Choose a single review to showcase</span>
						<span className="text-sm font-medium text-gray-600 flex items-center gap-1 shrink-0">
							Select
							<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
							</svg>
						</span>
					</div>
				</>
			)}
		</button>
	);
}

function getAggregationFilterSummary(filters: ReviewSelectionFilters | null): string {
	if (!filters?.groups || Object.keys(filters.groups).length === 0) return "No filters";

	const summaryParts: string[] = [];

	if (filters.groups.rating) {
		const { value, operator } = filters.groups.rating;
		if (operator === "gte" && typeof value === "number") {
			summaryParts.push(`${value}+ stars`);
		}
	}

	if (filters.groups.type) {
		const { value } = filters.groups.type;
		if (typeof value === "string") {
			summaryParts.push(value.charAt(0).toUpperCase() + value.slice(1));
		}
	}

	if (filters.groups.content_type) {
		const { value } = filters.groups.content_type;
		if (typeof value === "string") {
			const types = value.split(",").map((t) => t.trim().charAt(0).toUpperCase() + t.trim().slice(1));
			summaryParts.push(types.join(", "));
		}
	}

	if (filters.groups.source) {
		const { value } = filters.groups.source;
		if (typeof value === "string") {
			const sources = value.split(",");
			if (sources.length === 1) {
				summaryParts.push(sources[0].charAt(0).toUpperCase() + sources[0].slice(1).replace(/_/g, " "));
			} else {
				summaryParts.push(`${sources.length} sources`);
			}
		}
	}

	if (filters.groups.date) {
		const { operator, value } = filters.groups.date;
		if (typeof value === "string") {
			if (operator === "after") {
				summaryParts.push("Recent");
			} else if (operator === "between") {
				summaryParts.push("Date range");
			}
		}
	}

	if (filters.groups.tags) {
		const { value } = filters.groups.tags;
		if (typeof value === "string") {
			const tagCount = value.split(",").length;
			summaryParts.push(`${tagCount} tag${tagCount > 1 ? "s" : ""}`);
		} else if (Array.isArray(value)) {
			summaryParts.push(`${value.length} tag${value.length > 1 ? "s" : ""}`);
		}
	}

	return summaryParts.length > 0 ? summaryParts.join(", ") : "No filters";
}

function AggregatedDataSummary({
	filters,
	aggregationMetrics,
	onClick,
	disabled = false,
}: {
	filters: ReviewSelectionFilters | null;
	aggregationMetrics: WidgetPreviewAggregation | null;
	onClick: () => void;
	disabled?: boolean;
}) {
	const filterSummary = getAggregationFilterSummary(filters);

	return (
		<button
			className={`w-full p-4 rounded-xl border border-gray-200 text-left transition-all ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-gray-300 hover:bg-gray-50"}`}
			onClick={onClick}
			disabled={disabled}
		>
			<div className="flex items-center justify-between mb-2">
				<span className="text-sm font-medium text-gray-900">Average Rating</span>
				{aggregationMetrics && (
					<div className="flex items-center gap-2">
						<div className="flex items-center gap-0.5">
							{[1, 2, 3, 4, 5].map((star) => (
								<svg key={star} className={`w-3.5 h-3.5 ${star <= Math.round(aggregationMetrics.value) ? "text-amber-400" : "text-gray-200"}`} fill="currentColor" viewBox="0 0 20 20">
									<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
								</svg>
							))}
						</div>
						<span className="text-sm font-semibold text-gray-900">{Number(aggregationMetrics.value).toFixed(1)}</span>
					</div>
				)}
			</div>
			<div className="flex flex-col gap-2">
				{aggregationMetrics && (
					<span className="text-sm text-tertiary">
						Based on {aggregationMetrics.count} review{aggregationMetrics.count !== 1 ? "s" : ""}
					</span>
				)}
				<span className="text-sm text-tertiary truncate pr-2">Filter: {filterSummary}</span>
				<span className="text-sm font-medium text-gray-600 flex items-center gap-1 shrink-0">
					Configure
					<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
					</svg>
				</span>
			</div>
		</button>
	);
}

const hexColorRegex = /^#([A-Fa-f0-9]{8}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

function ColorField({ fieldKey, field, value, onChange }: { fieldKey: string; field: WidgetSchemaField; value: unknown; onChange: (key: string, value: unknown) => void }) {
	const hexValue = typeof value === "string" && hexColorRegex.test(value) ? value : "#000000";
	const [color, setColor] = useColor(hexValue);
	const [isOpen, setIsOpen] = useState(false);
	const [inputValue, setInputValue] = useState(hexValue.replace("#", ""));
	const [error, setError] = useState<string | undefined>(undefined);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const [openAbove, setOpenAbove] = useState(false);

	// Sync from external value changes
	useEffect(() => {
		const normalized = typeof value === "string" && hexColorRegex.test(value) ? value : "#000000";
		setColor(ColorService.convert("hex", normalized));
		setInputValue(normalized.replace("#", ""));
	}, [value, setColor]);

	// Click outside to close
	useEffect(() => {
		if (!isOpen) return;
		function handleClickOutside(event: MouseEvent) {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen]);

	const handlePickerChange = (c: typeof color) => {
		setColor(c);
		setInputValue(c.hex.replace("#", ""));
		setError(undefined);
		onChange(fieldKey, c.hex);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value.replace(/#/g, "");
		setInputValue(raw);
		const withHash = `#${raw}`;
		if (hexColorRegex.test(withHash)) {
			setError(undefined);
			const converted = ColorService.convert("hex", withHash);
			setColor(converted);
			onChange(fieldKey, converted.hex);
		} else if (raw.trim()) {
			setError("Invalid hex color");
		}
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const raw = e.clipboardData.getData("text").replace(/#/g, "");
		setInputValue(raw);
		const withHash = `#${raw}`;
		if (hexColorRegex.test(withHash)) {
			setError(undefined);
			const converted = ColorService.convert("hex", withHash);
			setColor(converted);
			onChange(fieldKey, converted.hex);
		} else if (raw.trim()) {
			setError("Invalid hex color");
		}
	};

	const handleBlur = () => {
		if (!inputValue.trim()) {
			setInputValue("000000ff");
			setError(undefined);
			const converted = ColorService.convert("hex", "#000000ff");
			setColor(converted);
			onChange(fieldKey, "#000000ff");
			return;
		}
		const withHash = `#${inputValue}`;
		if (!hexColorRegex.test(withHash)) {
			setError("Invalid hex color");
		}
	};

	return (
		<div>
			{field.label && <label className="block text-sm font-medium text-primary mb-1.5">{field.label}</label>}
			<div className="relative" ref={dropdownRef}>
				<div className="flex items-center gap-3">
					<button
						ref={triggerRef}
						type="button"
						onClick={() => {
							if (!isOpen && triggerRef.current) {
								const rect = triggerRef.current.getBoundingClientRect();
								const spaceBelow = window.innerHeight - rect.bottom;
								setOpenAbove(spaceBelow < 320);
							}
							setIsOpen(!isOpen);
						}}
						className={`w-10 h-10 rounded-lg shadow-sm border-2 transition-all duration-200 cursor-pointer shrink-0 hover:scale-105 hover:shadow-md ${
							error ? "border-red-500" : "border-gray-200 hover:border-gray-300"
						}`}
						style={{ backgroundColor: color.hex }}
						aria-label={`Select ${(field.label || fieldKey).toLowerCase()} color`}
					/>
					<Input
						sizeVariant="default"
						value={inputValue}
						onChange={handleInputChange}
						onPaste={handlePaste}
						onBlur={handleBlur}
						className="font-mono w-32 text-sm"
						placeholder="000000FF"
						error={error}
						inputPrefix={<span className="text-sm text-gray-400">#</span>}
					/>
				</div>
				{isOpen && (
					<div
						className={`absolute z-50 p-2 rounded-xl shadow-xl border bg-white border-gray-200 w-64 animate-in fade-in duration-200 ${
							openAbove ? "bottom-full mb-2 slide-in-from-bottom-2" : "mt-2 slide-in-from-top-2"
						}`}
					>
						<ColorPicker color={color} onChange={handlePickerChange} hideInput />
					</div>
				)}
			</div>
			{field.help_text && <p className="text-xs text-tertiary mt-1">{field.help_text}</p>}
		</div>
	);
}

// --- Conditional visibility helpers ---

interface WidgetAllConfigs {
	content: Record<string, unknown>;
	layout: Record<string, unknown>;
}

function getWidgetConfigValue(configs: WidgetAllConfigs, path: string): unknown {
	const [schema, ...fieldParts] = path.split(".");
	const field = fieldParts.join(".");
	if (schema === "layout") return configs.layout[field];
	if (schema === "content") return configs.content[field];
	return undefined;
}

function evaluateWidgetConditions(conditions: Record<string, unknown> | undefined, configs: WidgetAllConfigs): boolean {
	if (!conditions || Object.keys(conditions).length === 0) return false;
	for (const [path, expectedValue] of Object.entries(conditions)) {
		if (getWidgetConfigValue(configs, path) !== expectedValue) return false;
	}
	return true;
}

function isWidgetFieldVisible(field: WidgetSchemaField, configs: WidgetAllConfigs): boolean {
	if (!field.conditions?.visible_if) return true;
	return evaluateWidgetConditions(field.conditions.visible_if, configs);
}

function isWidgetGroupVisible(group: import("../types").WidgetLayoutGroup, configs: WidgetAllConfigs): boolean {
	if (!group.conditions?.visible_if) return true;
	return evaluateWidgetConditions(group.conditions.visible_if, configs);
}

// Resource options cache type for dynamic select fields
type ResourceOptionsCache = Record<string, Array<{ value: string; label: string }>>;

function DynamicSelectField({
	fieldKey,
	field,
	value,
	onChange,
	resourceOptions,
}: {
	fieldKey: string;
	field: WidgetSchemaField;
	value: unknown;
	onChange: (key: string, value: unknown) => void;
	resourceOptions: ResourceOptionsCache;
}) {
	const options = useMemo(() => {
		if (field.options_source) {
			const resourceKey = field.options_source.resource;
			return resourceOptions[resourceKey] || [];
		}
		return field.options || [];
	}, [field.options, field.options_source, resourceOptions]);

	const isLoading = field.options_source && !resourceOptions[field.options_source.resource];

	return (
		<div>
			<Select
				label={field.label}
				options={options}
				value={String(value ?? "")}
				onChange={(v) => onChange(fieldKey, v)}
				placeholder={isLoading ? "Loading..." : field.label}
				disabled={isLoading}
			/>
			{field.help_text && <p className="text-xs text-tertiary mt-1">{field.help_text}</p>}
		</div>
	);
}

function DynamicSearchableSelectField({
	fieldKey,
	field,
	value,
	onChange,
	resourceOptions,
}: {
	fieldKey: string;
	field: WidgetSchemaField;
	value: unknown;
	onChange: (key: string, value: unknown) => void;
	resourceOptions: ResourceOptionsCache;
}) {
	const options = useMemo(() => {
		if (field.options_source) {
			const resourceKey = field.options_source.resource;
			return resourceOptions[resourceKey] || [];
		}
		return field.options || [];
	}, [field.options, field.options_source, resourceOptions]);

	const isLoading = field.options_source && !resourceOptions[field.options_source.resource];

	return (
		<div>
			<FontSelect
				label={field.label}
				options={options}
				value={String(value ?? "")}
				onChange={(v) => onChange(fieldKey, v)}
				placeholder={isLoading ? "Loading..." : `Select ${field.label.toLowerCase()}...`}
				disabled={isLoading}
			/>
			{field.help_text && <p className="text-xs text-tertiary mt-1">{field.help_text}</p>}
		</div>
	);
}

function DynamicSchemaField({
	fieldKey,
	field,
	value,
	onChange,
	resourceOptions,
}: {
	fieldKey: string;
	field: WidgetSchemaField;
	value: unknown;
	onChange: (key: string, value: unknown) => void;
	resourceOptions?: ResourceOptionsCache;
}) {
	switch (field.type) {
		case "boolean":
			return <Toggle label={field.label} description={field.help_text} checked={Boolean(value)} onChange={(e) => onChange(fieldKey, e.target.checked)} />;
		case "select": {
			if (field.options_source) {
				return <DynamicSelectField fieldKey={fieldKey} field={field} value={value} onChange={onChange} resourceOptions={resourceOptions || {}} />;
			}
			const options = field.options ? field.options : field.in ? field.in.map((v) => ({ value: v, label: v })) : [];
			return (
				<div>
					<Select label={field.label} options={options} value={String(value ?? "")} onChange={(v) => onChange(fieldKey, v)} placeholder={field.label} />
					{field.help_text && <p className="text-xs text-tertiary mt-1">{field.help_text}</p>}
				</div>
			);
		}
		case "select-searchable":
			return <DynamicSearchableSelectField fieldKey={fieldKey} field={field} value={value} onChange={onChange} resourceOptions={resourceOptions || {}} />;
		case "tile": {
			const tileOptions = field.options ? field.options : field.in ? field.in.map((v) => ({ value: v, label: v })) : [];
			const selectedValue = String(value ?? "");
			const tileColumns = field.ui?.columns ?? 2;
			return (
				<div>
					{field.label && <label className="block text-sm font-medium text-primary mb-1.5">{field.label}</label>}
					<div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${tileColumns}, minmax(0, 1fr))` }}>
						{tileOptions.map((option) => (
							<button
								key={option.value}
								type="button"
								onClick={() => onChange(fieldKey, option.value)}
								className={`p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
									selectedValue === option.value ? "border-gray-900 bg-gray-50" : "border-gray-200 hover:border-gray-300"
								}`}
							>
								<div className="flex items-center justify-between">
									<span className="font-medium text-gray-900">{option.label}</span>
									{selectedValue === option.value && (
										<svg className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
									)}
								</div>
							</button>
						))}
					</div>
					{field.help_text && <p className="text-xs text-tertiary mt-1.5">{field.help_text}</p>}
				</div>
			);
		}
		case "integer":
		case "number":
			return (
				<div>
					<Input
						id={`widget-${fieldKey}`}
						label={field.label}
						type="number"
						value={String(value ?? "")}
						min={field.min}
						max={field.max}
						onChange={(e) => onChange(fieldKey, field.type === "integer" ? parseInt(e.target.value, 10) : parseFloat(e.target.value))}
					/>
					{field.help_text && <p className="text-xs text-tertiary mt-1">{field.help_text}</p>}
				</div>
			);
		case "textarea":
			return (
				<div>
					<label htmlFor={`widget-${fieldKey}`} className="block text-sm font-medium text-primary mb-1.5">
						{field.label}
					</label>
					<textarea
						id={`widget-${fieldKey}`}
						className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-primary focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y"
						rows={3}
						value={String(value ?? "")}
						onChange={(e) => onChange(fieldKey, e.target.value)}
					/>
					{field.help_text && <p className="text-xs text-tertiary mt-1">{field.help_text}</p>}
				</div>
			);
		case "color":
			return <ColorField fieldKey={fieldKey} field={field} value={value} onChange={onChange} />;
		default:
			return (
				<div>
					<Input id={`widget-${fieldKey}`} label={field.label} value={String(value ?? "")} onChange={(e) => onChange(fieldKey, e.target.value)} />
					{field.help_text && <p className="text-xs text-tertiary mt-1">{field.help_text}</p>}
				</div>
			);
	}
}

function DynamicDesignPanel({
	templateDetail,
	layoutConfig,
	contentConfig,
	onUpdate,
	resourceOptions,
}: {
	templateDetail: WidgetTemplateDetail | null;
	layoutConfig: Record<string, unknown>;
	contentConfig: Record<string, unknown>;
	onUpdate: (key: string, value: unknown) => void;
	resourceOptions?: ResourceOptionsCache;
}) {
	const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

	useEffect(() => {
		if (!templateDetail?.layout_groups) return;
		const initial: Record<string, boolean> = {};
		for (const [groupKey, group] of Object.entries(templateDetail.layout_groups)) {
			initial[groupKey] = group.default_open;
		}
		setOpenGroups(initial);
	}, [templateDetail]);

	const allConfigs: WidgetAllConfigs = useMemo(() => ({ content: contentConfig, layout: layoutConfig }), [contentConfig, layoutConfig]);

	if (!templateDetail || !templateDetail.layout_schema || Object.keys(templateDetail.layout_schema).length === 0) {
		return (
			<div className="p-6">
				<p className="text-sm text-tertiary">No design settings available for this template.</p>
			</div>
		);
	}

	const groups = templateDetail.layout_groups;
	const schema = templateDetail.layout_schema;

	const sortedGroups = Object.entries(groups).sort(([, a], [, b]) => a.order - b.order);

	const fieldsByGroup: Record<string, [string, WidgetSchemaField][]> = {};
	for (const [fieldKey, field] of Object.entries(schema)) {
		const group = field.group || "default";
		if (!fieldsByGroup[group]) fieldsByGroup[group] = [];
		fieldsByGroup[group].push([fieldKey, field]);
	}

	const toggleGroup = (groupKey: string) => {
		setOpenGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
	};

	return (
		<div className="divide-y divide-gray-100">
			{sortedGroups.map(([groupKey, group]) => {
				// Skip groups that are conditionally hidden
				if (!isWidgetGroupVisible(group, allConfigs)) return null;

				const fields = fieldsByGroup[groupKey] || [];
				// Filter fields by visibility conditions
				const visibleFields = fields.filter(([, field]) => isWidgetFieldVisible(field, allConfigs));
				if (visibleFields.length === 0) return null;

				const isOpen = openGroups[groupKey] ?? group.default_open;

				if (group.accordion) {
					return (
						<div key={groupKey} className="p-6">
							<button type="button" onClick={() => toggleGroup(groupKey)} className="w-full flex items-center justify-between">
								<div>
									<h3 className="font-semibold text-gray-900 flex items-center gap-2">
										{/* <span className="text-gray-400">
											<IconColorSwatches />
										</span> */}
										{group.label}
									</h3>
									{group.description && <p className="text-xs text-tertiary mt-0.5">{group.description}</p>}
								</div>
								<svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
								</svg>
							</button>
							{isOpen && (
								<div className="space-y-5 mt-6">
									{visibleFields.map(([fieldKey, field]) => (
										<DynamicSchemaField key={fieldKey} fieldKey={fieldKey} field={field} value={layoutConfig[fieldKey]} onChange={onUpdate} resourceOptions={resourceOptions} />
									))}
								</div>
							)}
						</div>
					);
				}

				return (
					<div key={groupKey} className="p-6">
						<h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
							{/* <span className="text-gray-400">
								<IconColorSwatches />
							</span> */}
							{group.label}
						</h3>
						{group.description && <p className="text-xs text-tertiary mb-4">{group.description}</p>}
						<div className="space-y-5">
							{visibleFields.map(([fieldKey, field]) => (
								<DynamicSchemaField key={fieldKey} fieldKey={fieldKey} field={field} value={layoutConfig[fieldKey]} onChange={onUpdate} resourceOptions={resourceOptions} />
							))}
						</div>
					</div>
				);
			})}
		</div>
	);
}

export default function WidgetBuilder({
	brandUrl,
	tags,
	widgetData,
	pinnedReviewIds: initialPinnedReviewIds,
	curatedReviewIds: initialCuratedReviewIds,
	workspaceId,
	brandKit,
	showBranding = true,
	maxReviewsPerWidget,
	allowDynamicWidgets = true,
	userRole,
	onSave,
	justCreated,
}: WidgetBuilderProps) {
	const navigate = useNavigate();
	const { confirmNavigation } = useNavigationGuard();
	const { previewWidget, templates, templateDetail, listTemplates, showTemplate } = useWidgets();
	const { planDetails } = usePlan();

	const [activeTab, setActiveTab] = useState<TabType>("content");
	const [templateModalOpen, setTemplateModalOpen] = useState(false);
	const [selectReviewsModalOpen, setSelectReviewsModalOpen] = useState(false);
	const [selectPinnedReviewsModalOpen, setSelectPinnedReviewsModalOpen] = useState(false);
	const [selectSingleReviewModalOpen, setSelectSingleReviewModalOpen] = useState(false);
	const [selectStaticReviewsModalOpen, setSelectStaticReviewsModalOpen] = useState(false);
	const [selectAggregationFiltersModalOpen, setSelectAggregationFiltersModalOpen] = useState(false);
	const [embedCodeModalOpen, setEmbedCodeModalOpen] = useState(false);
	const [statusChangeConfirmOpen, setStatusChangeConfirmOpen] = useState(false);
	const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
	const [planLimitError, setPlanLimitError] = useState<WidgetPlanLimitError | null>(null);
	const [widgetCustomizationError, setWidgetCustomizationError] = useState<boolean>(false);
	const justCreatedRef = useRef(justCreated === true);
	const [selectedReview, setSelectedReview] = useState<Review | null>(null);
	const [isTitleFocused, setIsTitleFocused] = useState(false);
	const titleInputRef = useRef<HTMLInputElement>(null);

	const [initialState, setInitialState] = useState<BuilderState | null>(null);
	const [currentState, setCurrentState] = useState<BuilderState | null>(null);
	const currentStateRef = useRef<BuilderState | null>(null);
	currentStateRef.current = currentState;

	const [previewData, setPreviewData] = useState<WidgetPreviewResponse | null>(null);
	const [isPreviewLoading, setIsPreviewLoading] = useState(true);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [showMockReviews, setShowMockReviews] = useState(false);
	const debouncedPreviewRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const canCustomizeWidget = planDetails?.plan.features?.widget_customization;

	const [isSaving, setIsSaving] = useState(false);
	type SaveStatus = "idle" | "saving" | "saved" | "error";
	const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
	const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);

	// Resource options for dynamic select fields (e.g., fonts)
	const [resourceOptions, setResourceOptions] = useState<ResourceOptionsCache>({});
	const { fonts, fetchFonts } = useFonts();

	// Load resource options for dynamic select fields based on template schema
	useEffect(() => {
		if (!templateDetail) return;

		const requiredResources = new Set<string>();

		const scanSchema = (schema: Record<string, WidgetSchemaField> | undefined) => {
			if (!schema) return;
			for (const field of Object.values(schema)) {
				if ((field.type === "select" || field.type === "select-searchable") && field.options_source?.type === "system") {
					requiredResources.add(field.options_source.resource);
				}
			}
		};

		scanSchema(templateDetail.layout_schema);
		if (!Array.isArray(templateDetail.content_schema)) {
			scanSchema(templateDetail.content_schema as Record<string, WidgetSchemaField>);
		}

		for (const resource of requiredResources) {
			if (resourceOptions[resource]) continue;

			if (resource === "fonts") {
				fetchFonts();
			}
		}
	}, [templateDetail, fetchFonts, resourceOptions]);

	// Update resourceOptions when fonts are loaded
	useEffect(() => {
		if (!fonts || fonts.length === 0 || !templateDetail) return;

		// Find the options_source config for fonts to get label_key and value_key
		const findFontOptionsSource = (schema: Record<string, WidgetSchemaField> | undefined) => {
			if (!schema) return null;
			for (const field of Object.values(schema)) {
				if ((field.type === "select" || field.type === "select-searchable") && field.options_source?.resource === "fonts") {
					return field.options_source;
				}
			}
			return null;
		};

		const optionsSource =
			findFontOptionsSource(templateDetail.layout_schema) ||
			(!Array.isArray(templateDetail.content_schema) ? findFontOptionsSource(templateDetail.content_schema as Record<string, WidgetSchemaField>) : null);

		if (optionsSource) {
			const options = fonts.map((font) => ({
				value: String(font[optionsSource.value_key as keyof typeof font] || ""),
				label: String(font[optionsSource.label_key as keyof typeof font] || ""),
			}));

			setResourceOptions((prev) => ({
				...prev,
				fonts: options,
			}));
		}
	}, [fonts, templateDetail]);

	const isDirty = useMemo(() => {
		if (!initialState || !currentState) return false;
		// Exclude preview-derived count fields from dirty comparison — these are
		// updated by the preview endpoint and don't represent user changes.
		const keys: (keyof BuilderState)[] = [
			"title",
			"type",
			"display_type",
			"template",
			"template_version",
			"content_config",
			"layout_config",
			"filters",
			"sort",
			"aggregation_config",
			"reviewIds",
			"pinnedReviewIds",
			"status",
		];
		return keys.some((key) => JSON.stringify(initialState[key]) !== JSON.stringify(currentState[key]));
	}, [initialState, currentState]);

	useDirtyState({ hasUnsavedChanges: isDirty });

	// Fetch templates list on mount
	useEffect(() => {
		if (!templates) {
			listTemplates();
		}
	}, [templates, listTemplates]);

	// Fetch template detail when widget data loads or template changes
	useEffect(() => {
		if (currentState?.template && currentState?.template_version) {
			showTemplate(currentState.template, currentState.template_version);
		}
	}, [currentState?.template, currentState?.template_version, showTemplate]);

	// Load custom font with all required weights for preview
	useEffect(() => {
		if (!currentState) return;
		const { layout_config } = currentState;
		if (layout_config.custom_font !== true) return;
		const fontBody = typeof layout_config.font_body === "string" ? layout_config.font_body : "";
		if (!fontBody) return;
		const weights = resolveWeights(layout_config, templateDetail);
		loadGoogleFont(fontBody, weights);
	}, [currentState?.layout_config?.custom_font, currentState?.layout_config?.font_body, currentState?.layout_config?.font_body_weights, templateDetail]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!widgetData) return;

		const state: BuilderState = {
			title: widgetData.title || "Untitled Widget",
			type: widgetData.type || "dynamic",
			display_type: widgetData.display_type,
			template: widgetData.template,
			template_version: widgetData.template_version,
			content_config: widgetData.content_config || {},
			layout_config: widgetData.layout_config || {},
			filters: widgetData.filters || null,
			sort: widgetData.sort || null,
			aggregation_config: widgetData.aggregation_config || (widgetData.display_type === "aggregation" ? { metric: "average_rating", precision: 1 } : null),
			reviewIds: initialCuratedReviewIds ?? [],
			pinnedReviewIds: initialPinnedReviewIds ?? [],
			status: widgetData.status || "unpublished",
			reviewCounts: {
				total: (initialPinnedReviewIds?.length ?? 0) + (initialCuratedReviewIds?.length ?? 0),
				pinned: initialPinnedReviewIds?.length ?? 0,
				curated: initialCuratedReviewIds?.length ?? 0,
				unpinned: 0,
			},
		};

		setInitialState(state);
		setCurrentState(state);
	}, [widgetData, initialPinnedReviewIds, initialCuratedReviewIds]);

	useEffect(() => {
		if (!currentState) return;
		if (!validationErrors) return;
		setValidationErrors(null);
	}, [currentState, setValidationErrors, validationErrors]);

	const handleBackClick = () => {
		if (confirmNavigation()) {
			navigate(`/${brandUrl}/widgets`);
		}
	};

	const handleTitleChange = (title: string) => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return { ...prev, title };
		});
	};

	const handleStatusChange = (status: "published" | "unpublished") => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return { ...prev, status };
		});
	};

	const handleTemplateConfirm = async (templateName: string, version: number) => {
		// Find the template from API templates to get the display type
		const apiTemplate = templates?.find((t) => t.template === templateName && t.version === version);
		const newType: WidgetDisplayType = apiTemplate?.display_type || "collection";

		// Fetch the new template detail to get defaults
		const detailResponse = await showTemplate(templateName, version);
		const defaults = detailResponse?.data?.defaults;

		setPreviewData(null);
		setCurrentState((prev) => {
			if (!prev) return null;

			const isTypeChange = prev.display_type !== newType;
			return {
				...prev,
				template: templateName,
				template_version: version,
				display_type: newType,
				type: newType === "single" ? "static" : newType === "aggregation" ? "dynamic" : prev.type,
				layout_config: defaults?.layout ? { ...defaults.layout } : {},
				content_config: Array.isArray(defaults?.content) ? {} : defaults?.content ? { ...defaults.content } : {},
				filters: isTypeChange && newType === "single" ? null : prev.filters,
				sort: isTypeChange && newType === "single" ? null : prev.sort,
				aggregation_config: newType === "aggregation" ? prev.aggregation_config || { metric: "average_rating", precision: 1 } : null,
				reviewIds: prev.reviewIds,
				pinnedReviewIds: prev.pinnedReviewIds,
				reviewCounts: isTypeChange
					? {
							total: 0,
							pinned: prev.reviewCounts.pinned,
							curated: prev.reviewCounts.curated,
							unpinned: 0,
						}
					: prev.reviewCounts,
			};
		});
	};

	const handleLayoutConfigUpdate = (key: string, value: unknown) => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return { ...prev, layout_config: { ...prev.layout_config, [key]: value } };
		});
	};

	const handleReviewsSave = (filters: ReviewSelectionFilters | null, sort: ReviewSelectionSort, totalMatchingCount: number) => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return { ...prev, filters, sort, reviewCounts: { ...prev.reviewCounts, unpinned: totalMatchingCount } };
		});
	};

	const handlePinnedReviewsSave = (pinnedIds: string[], totalMatchingCount: number) => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return { ...prev, pinnedReviewIds: pinnedIds, reviewCounts: { ...prev.reviewCounts, unpinned: totalMatchingCount } };
		});
	};

	const handleAggregationFiltersSave = (filters: ReviewSelectionFilters | null) => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return { ...prev, filters };
		});
	};

	const handleSingleReviewSave = (reviewId: string | null, review: Review | null) => {
		setSelectedReview(review);
		setCurrentState((prev) => {
			if (!prev) return null;
			return { ...prev, reviewIds: reviewId ? [reviewId] : [], reviewCounts: { ...prev.reviewCounts, curated: reviewId ? 1 : 0 } };
		});
	};

	const handleStaticReviewsSave = (selectedIds: string[], _selectedReviews: Review[]) => {
		setCurrentState((prev) => {
			if (!prev) return null;
			return { ...prev, reviewIds: selectedIds, reviewCounts: { ...prev.reviewCounts, curated: selectedIds.length } };
		});
	};

	const buildPreviewPayload = (state: BuilderState): WidgetPreviewRequest => {
		const base = { type: state.type, display_type: state.display_type };
		const effectiveFilters = state.filters ?? DEFAULT_DYNAMIC_FILTERS;

		if (state.display_type === "single") {
			const singleReviewId = state.reviewIds.length > 0 ? state.reviewIds[0] : undefined;
			return { ...base, review_ids: singleReviewId ? [singleReviewId] : [] };
		}
		if (state.type === "static" && state.display_type === "collection") {
			return { ...base, review_ids: state.reviewIds };
		}
		if (state.type === "static" && state.display_type === "aggregation") {
			return { ...base, aggregation_config: state.aggregation_config, review_ids: state.reviewIds };
		}
		if (state.type === "dynamic" && state.display_type === "aggregation") {
			return { ...base, aggregation_config: state.aggregation_config, sort: state.sort, filters: effectiveFilters };
		}
		// dynamic + collection (default)
		return { ...base, sort: state.sort, filters: effectiveFilters, review_pinned_ids: state.pinnedReviewIds };
	};

	const fetchPreview = useCallback(async () => {
		const state = currentStateRef.current;
		if (!workspaceId || !state) return;

		setIsPreviewLoading(true);
		try {
			const payload = buildPreviewPayload(state);

			const response = await previewWidget(workspaceId, payload);
			if (response.success && response.data) {
				setPreviewData(response.data);

				if ("reviews" in response.data && response.data.reviews?.meta?.counts) {
					const counts = response.data.reviews.meta.counts;
					setCurrentState((prev) => {
						if (!prev) return null;
						return { ...prev, reviewCounts: counts };
					});
				}
			}
		} finally {
			setIsPreviewLoading(false);
		}
	}, [workspaceId, previewWidget]);

	const nextCursor = useMemo(() => {
		if (previewData && "reviews" in previewData) return previewData.reviews?.pagination?.next_cursor ?? null;
		return null;
	}, [previewData]);

	const handleLoadMore = useCallback(async () => {
		const state = currentStateRef.current;
		if (!workspaceId || !state || !nextCursor) return;

		setIsLoadingMore(true);
		try {
			const payload = buildPreviewPayload(state);

			const response = await previewWidget(workspaceId, payload, nextCursor);
			if (response.success && response.data && "reviews" in response.data && response.data.reviews && previewData && "reviews" in previewData && previewData.reviews) {
				setPreviewData({
					...response.data,
					reviews: {
						...response.data.reviews,
						data: [...previewData.reviews.data, ...response.data.reviews.data],
					},
				});
			}
		} finally {
			setIsLoadingMore(false);
		}
	}, [workspaceId, nextCursor, previewWidget, previewData]);

	const triggerPreview = useCallback(() => {
		if (debouncedPreviewRef.current) {
			clearTimeout(debouncedPreviewRef.current);
		}
		debouncedPreviewRef.current = setTimeout(() => {
			fetchPreview();
		}, PREVIEW_DEBOUNCE_MS);
	}, [fetchPreview]);

	useEffect(() => {
		if (currentState) {
			triggerPreview();
		}
	}, [
		triggerPreview,
		currentState?.type,
		currentState?.display_type,
		currentState?.filters,
		currentState?.sort,
		currentState?.pinnedReviewIds,
		currentState?.reviewIds,
		currentState?.aggregation_config,
		currentState?.template,
		currentState?.template_version,
	]);

	useEffect(() => {
		return () => {
			if (debouncedPreviewRef.current) {
				clearTimeout(debouncedPreviewRef.current);
			}
			if (savedTimeoutRef.current) {
				clearTimeout(savedTimeoutRef.current);
			}
		};
	}, []);

	const isStatusChangingFromPublished = initialState?.status === "published" && currentState?.status !== "published";
	const isStatusChangingToPublished = initialState?.status !== "published" && currentState?.status === "published";

	const handleSaveClick = () => {
		if (isStatusChangingFromPublished || isStatusChangingToPublished) {
			setStatusChangeConfirmOpen(true);
		} else {
			handleSave();
		}
	};

	const handleSave = async () => {
		if (!isDirty || !currentState || !initialState) return;

		if (savedTimeoutRef.current) {
			clearTimeout(savedTimeoutRef.current);
		}

		setIsSaving(true);
		setSaveStatus("saving");
		setValidationErrors(null);

		const effectiveFilters = currentState.display_type === "single" ? null : (currentState.filters ?? DEFAULT_DYNAMIC_FILTERS);
		const payload: WidgetUpdateRequest = {
			title: currentState.title,
			type: currentState.type,
			display_type: currentState.display_type,
			template: currentState.template,
			template_version: currentState.template_version,
			content_config: currentState.content_config,
			layout_config: currentState.layout_config,
			filters: effectiveFilters,
			sort: currentState.sort,
			aggregation_config: currentState.aggregation_config,
			status: currentState.status,
		};

		const selectedReviewsChanged = JSON.stringify(currentState.reviewIds) !== JSON.stringify(initialState.reviewIds);
		const pinnedReviewsChanged = JSON.stringify(currentState.pinnedReviewIds) !== JSON.stringify(initialState.pinnedReviewIds);

		try {
			const result = await onSave(payload, {
				widgetType: currentState.display_type,
				widgetMode: currentState.type,
				selectedReviewIds: currentState.reviewIds,
				pinnedReviewIds: currentState.pinnedReviewIds,
				selectedReviewsChanged,
				pinnedReviewsChanged,
			});
			if (!result.success && result.validationErrors) {
				setValidationErrors(result.validationErrors);
				setSaveStatus("error");
				savedTimeoutRef.current = setTimeout(() => {
					setSaveStatus("idle");
				}, 3000);
				return;
			}
			if (!result.success) {
				setSaveStatus("error");
				savedTimeoutRef.current = setTimeout(() => {
					setSaveStatus("idle");
				}, 3000);
				return;
			}

			setInitialState(currentState);
			setStatusChangeConfirmOpen(false);
			setSaveStatus("saved");
			savedTimeoutRef.current = setTimeout(() => {
				setSaveStatus("idle");
			}, 2000);

			if (justCreatedRef.current) {
				justCreatedRef.current = false;
				setEmbedCodeModalOpen(true);
			}
		} catch (error) {
			console.error("Failed to save widget:", error);
			setSaveStatus("error");
			savedTimeoutRef.current = setTimeout(() => {
				setSaveStatus("idle");
			}, 3000);
		} finally {
			setIsSaving(false);
		}
	};

	const handleDiscard = () => {
		if (initialState) {
			setCurrentState(structuredClone(initialState));
		}
	};

	const reviewFilters = useMemo(() => convertSelectionFiltersToReviewFilters(currentState?.filters ?? null), [currentState?.filters]);

	// Find current template from API templates
	const selectedApiTemplate: WidgetTemplateListItem | null = useMemo(() => {
		if (!templates || !currentState) return null;
		return templates.find((t) => t.template === currentState.template && t.version === currentState.template_version) ?? null;
	}, [templates, currentState]);

	const hasPreviewReviews = useMemo(() => {
		if (!previewData) return false;
		if ("metrics" in previewData) return true;
		if ("reviews" in previewData) return (previewData.reviews?.data?.length || 0) > 0;
		return false;
	}, [previewData]);

	// Mock reviews for preview mode
	const mockReviews = useMemo(() => getMockReviews(), []);

	// Create mock preview data
	const mockPreviewData = useMemo(() => {
		if (mockReviews.length === 0) return null;

		if (currentState?.display_type === "aggregation") {
			return {
				type: "dynamic" as const,
				display_type: "aggregation" as const,
				metrics: {
					metric: "average_rating",
					value: 4.8,
					precision: 1,
					count: mockReviews.length,
				} satisfies WidgetPreviewAggregation,
			};
		}

		return {
			type: currentState?.type === "dynamic" ? ("dynamic" as const) : ("static" as const),
			display_type: currentState?.display_type === "single" ? ("single" as const) : ("collection" as const),
			reviews: {
				data: currentState?.display_type === "single" ? [mockReviews[0]] : mockReviews.slice(0, 6),
				pagination: { next_cursor: null, has_more: false },
				meta: { counts: { total: mockReviews.length, pinned: 0, curated: mockReviews.length, unpinned: mockReviews.length } },
			},
		};
	}, [mockReviews, currentState?.display_type, currentState?.type]);

	// Determine which preview data to display
	const displayedPreviewData = showMockReviews && !hasPreviewReviews ? mockPreviewData : previewData;

	// Turn off mock reviews when real reviews are available
	useEffect(() => {
		if (hasPreviewReviews) {
			setShowMockReviews(false);
		}
	}, [hasPreviewReviews]);

	if (!currentState) {
		return null;
	}

	// Show blocked message if widget is blocked by plan
	if (widgetData?.blocked_by_plan) {
		return (
			<div className="flex items-center justify-center min-h-[calc(100vh-4rem)] bg-[#fafafa]">
				<div className="text-center max-w-full px-6">
					<div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
						<svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
							<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
						</svg>
					</div>
					<h2 className="text-xl font-semibold text-primary mb-2">Feature Unavailable</h2>
					<p className="text-tertiary mb-6">
						This widget is not available for your current plan.
						<br />
						Please upgrade to enable it.
					</p>
					<button
						type="button"
						onClick={() => navigate(`/${brandUrl}/billing`)}
						className="px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer bg-brand text-white hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1"
					>
						Upgrade Now
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="flex h-screen bg-[#fafafa]">
			<div className="w-100 shrink-0 bg-white border-r border-gray-200 flex flex-col h-screen">
				<div className="px-6 py-4 border-b border-gray-200">
					<div className="flex items-center gap-3">
						<Button onClick={handleBackClick} variant="soft" className="p-2">
							<IconArrowLeftLane className="w-5 h-5" />
						</Button>
						<div className="flex-1 min-w-0">
							{isTitleFocused ? (
								<input
									ref={titleInputRef}
									type="text"
									value={currentState.title}
									onChange={(e) => handleTitleChange(e.target.value)}
									onBlur={() => setIsTitleFocused(false)}
									autoComplete="off"
									data-1p-ignore
									data-lpignore="true"
									data-bwignore
									data-form-type="other"
									name="widget-title"
									className="font-heading text-base font-semibold bg-transparent border-none outline-none rounded px-1 -ml-1 w-full text-gray-900 hover:outline-1 hover:outline-gray-300 focus:outline-1 focus:outline-gray-400"
								/>
							) : (
								<h1
									onClick={() => {
										setIsTitleFocused(true);
										setTimeout(() => titleInputRef.current?.focus(), 0);
									}}
									className="text-base font-semibold truncate cursor-text rounded px-1 -ml-1 w-full text-gray-900 hover:outline-1 hover:outline-gray-300 focus:outline-1 focus:outline-gray-400"
								>
									{currentState.title || "Widget"}
								</h1>
							)}
						</div>
					</div>
				</div>

				<div className="flex border-b border-gray-200">
					{[
						{
							id: "content",
							label: "Content",
							isPremium: false,
							icon: (
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
									/>
								</svg>
							),
						},
						{
							id: "design",
							label: "Design",
							isPremium: !canCustomizeWidget,
							icon: (
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
									/>
								</svg>
							),
						},
					].map((tab) => (
						<button
							key={tab.id}
							type="button"
							onClick={() => {
								if (tab.isPremium) {
									setUpgradeModalOpen(true);
									setWidgetCustomizationError(true);
									return;
								}
								setActiveTab(tab.id as TabType);
							}}
							className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative ${
								activeTab === tab.id ? "text-gray-900" : "text-gray-500 hover:text-gray-700"
							}`}
						>
							{tab.icon}
							{tab.label}
							{tab.isPremium && (
								<span className="inline-block p-1 text-blue-600 rounded-lg bg-blue-50 border border-blue-200 ml-1">
									<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
										<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
									</svg>
								</span>
							)}
							{activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" />}
						</button>
					))}
				</div>

				<div className="flex-1 overflow-y-auto" style={{ scrollPadding: "1.5rem" }}>
					{activeTab === "content" && (
						<div className="divide-y divide-gray-100">
							<div className="p-6">
								<h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
									<IconTemplate className="w-4 h-4 text-gray-400" />
									Template
								</h3>
								<WidgetTemplateSelector selectedTemplate={selectedApiTemplate} selectedTemplateName={currentState.template} onClick={() => setTemplateModalOpen(true)} />
								{selectedApiTemplate?.max_reviews != null && <p className="text-sm text-tertiary mt-2">This template displays up to {selectedApiTemplate.max_reviews} reviews.</p>}
							</div>

							<div className="p-6">
								<h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
									<span className="text-gray-400">
										<IconChatBubbleSquare />
									</span>
									Select Reviews
								</h3>

								{currentState.display_type === "single" && (
									<SingleReviewSelectionSummary
										review={currentState.reviewIds.length > 0 ? selectedReview || (previewData && "reviews" in previewData ? previewData.reviews?.data?.[0] : null) || null : null}
										onClick={() => setSelectSingleReviewModalOpen(true)}
										disabled={showMockReviews}
									/>
								)}

								{currentState.display_type === "collection" && (
									<div className="space-y-4">
										{/* Mode selector */}
										<div>
											<label className="text-sm font-medium text-gray-700 mb-2 block">Mode</label>
											<div className="grid grid-cols-2 gap-3">
												<button
													type="button"
													onClick={() => {
														if (!allowDynamicWidgets) {
															setUpgradeModalOpen(true);
														} else {
															setCurrentState((prev) => (prev ? { ...prev, type: "dynamic" } : prev));
														}
													}}
													disabled={showMockReviews}
													className={`p-4 rounded-xl border-2 text-left transition-all ${
														showMockReviews
															? "opacity-50 cursor-not-allowed border-gray-200"
															: currentState.type === "dynamic" && allowDynamicWidgets
																? "border-gray-900 bg-gray-50 cursor-pointer hover:border-gray-300"
																: "border-gray-200 cursor-pointer hover:border-gray-300"
													}`}
												>
													<div className="flex items-center justify-between mb-1">
														<span className={`font-medium ${!allowDynamicWidgets ? "text-gray-400" : "text-gray-900"}`}>Dynamic</span>
														{currentState.type === "dynamic" && allowDynamicWidgets && (
															<svg className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
																<path
																	fillRule="evenodd"
																	d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
																	clipRule="evenodd"
																/>
															</svg>
														)}
														{!allowDynamicWidgets && (
															<svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
																<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
															</svg>
														)}
													</div>
													<span className={`text-sm ${!allowDynamicWidgets ? "text-gray-400" : "text-tertiary"}`}>Live updates</span>
													{!allowDynamicWidgets && (
														<div className="mt-2 flex items-center gap-1.5">
															<span className="text-xs text-blue-600">Upgrade to enable</span>
														</div>
													)}
												</button>
												<button
													type="button"
													onClick={() => setCurrentState((prev) => (prev ? { ...prev, type: "static" } : prev))}
													disabled={showMockReviews}
													className={`p-4 rounded-xl border-2 text-left transition-all ${
														showMockReviews
															? "opacity-50 cursor-not-allowed border-gray-200"
															: currentState.type === "static"
																? "border-gray-900 bg-gray-50 cursor-pointer hover:border-gray-300"
																: "border-gray-200 cursor-pointer hover:border-gray-300"
													}`}
												>
													<div className="flex items-center justify-between mb-1">
														<span className="font-medium text-gray-900">Static</span>
														{currentState.type === "static" && (
															<svg className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
																<path
																	fillRule="evenodd"
																	d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
																	clipRule="evenodd"
																/>
															</svg>
														)}
													</div>
													<span className="text-sm text-tertiary">Manually curated</span>
												</button>
											</div>
											<p className="text-sm text-tertiary mt-3">
												{currentState.type === "dynamic" && allowDynamicWidgets
													? "Dynamic widgets automatically update when new testimonials match your filters."
													: currentState.type === "dynamic" && !allowDynamicWidgets
														? "Dynamic widgets automatically update with your latest reviews. Upgrade to Growth to enable this feature."
														: "Static widgets show only testimonials you select."}
											</p>
										</div>

										{currentState.type === "dynamic" && allowDynamicWidgets && (
											<>
												<ReviewPinnedSelectionSummary
													pinnedCount={currentState.pinnedReviewIds.length}
													maxReviews={selectedApiTemplate?.max_reviews}
													onClick={() => setSelectPinnedReviewsModalOpen(true)}
													disabled={showMockReviews}
												/>
												<ReviewSelectionSummary
													matchingCount={currentState.reviewCounts.unpinned}
													activeFilters={currentState.filters}
													activeSort={currentState.sort}
													maxReviews={selectedApiTemplate?.max_reviews}
													pinnedCount={currentState.pinnedReviewIds.length}
													onClick={() => setSelectReviewsModalOpen(true)}
													disabled={showMockReviews}
												/>
											</>
										)}

										{currentState.type === "static" && (
											<StaticReviewSelectionSummary
												selectedCount={currentState.reviewCounts.curated}
												maxReviews={selectedApiTemplate?.max_reviews}
												onClick={() => setSelectStaticReviewsModalOpen(true)}
												disabled={showMockReviews}
											/>
										)}
									</div>
								)}

								{currentState.display_type === "aggregation" && (
									<AggregatedDataSummary
										filters={currentState.filters}
										aggregationMetrics={previewData && "metrics" in previewData ? previewData.metrics : null}
										onClick={() => setSelectAggregationFiltersModalOpen(true)}
										disabled={showMockReviews}
									/>
								)}
							</div>
						</div>
					)}

					{activeTab === "design" && (
						<DynamicDesignPanel
							templateDetail={templateDetail}
							layoutConfig={currentState.layout_config}
							contentConfig={currentState.content_config}
							onUpdate={handleLayoutConfigUpdate}
							resourceOptions={resourceOptions}
						/>
					)}
				</div>
			</div>

			<div className="fixed top-0 left-100 right-0 z-20 px-8 py-4 bg-white border-b border-gray-200">
				<div className="max-w-6xl mx-auto px-8">
					<div className="flex items-center justify-between gap-4">
						<p className="text-sm text-tertiary">
							{currentState.status === "published" ? "Your widget is published and ready to embed." : "Your widget is unpublished and hidden from public view."}
						</p>
						<div className="flex items-center gap-3">
							<DropdownMenu.Root>
								<DropdownMenu.Trigger asChild>
									<button
										type="button"
										className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg cursor-pointer transition-colors hover:bg-gray-100 border border-gray-200 ${
											currentState.status === "published" ? "text-green-700" : "text-gray-600"
										}`}
									>
										<span className={`w-2 h-2 rounded-full ${currentState.status === "published" ? "bg-green-500" : "bg-gray-400"}`} />
										{currentState.status === "published" ? "Published" : "Unpublished"}
										<svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
										</svg>
									</button>
								</DropdownMenu.Trigger>
								<DropdownMenu.Portal>
									<DropdownMenu.Content className="bg-white rounded-lg shadow-lg border border-gray-200 py-1.5 z-50 min-w-[140px]" sideOffset={5}>
										<DropdownMenu.Item
											className="flex items-center justify-between px-4 py-2.5 text-sm cursor-pointer outline-none hover:bg-gray-50"
											onSelect={() => handleStatusChange("published")}
										>
											<span className="text-gray-700">Published</span>
											{currentState.status === "published" && (
												<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
												</svg>
											)}
										</DropdownMenu.Item>
										<DropdownMenu.Item
											className="flex items-center justify-between px-4 py-2.5 text-sm cursor-pointer outline-none hover:bg-gray-50"
											onSelect={() => handleStatusChange("unpublished")}
										>
											<span className="text-gray-700">Unpublished</span>
											{currentState.status === "unpublished" && (
												<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
												</svg>
											)}
										</DropdownMenu.Item>
									</DropdownMenu.Content>
								</DropdownMenu.Portal>
							</DropdownMenu.Root>
							{currentState.status === "published" && widgetData?.id ? (
								<Button variant="light" className="flex items-center gap-2 shrink-0" onClick={() => setEmbedCodeModalOpen(true)}>
									<IconCodeMonitor />
									Embed Code
								</Button>
							) : (
								<Tooltip content="Embed code is only available when the widget is published.">
									<span>
										<Button variant="light" className="flex items-center gap-2 shrink-0" disabled>
											<IconCodeMonitor />
											Embed Code
										</Button>
									</span>
								</Tooltip>
							)}
						</div>
					</div>
				</div>
			</div>

			{!currentState.layout_config.custom_font && (
				<div className="fixed top-18 left-100 right-0 z-15 px-4 py-2 bg-blue-50 border-b border-blue-100">
					<div className="flex items-center justify-center gap-2 text-blue-700">
						<svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
						</svg>
						<p className="text-sm">This widget picks up your website's font when embedded, so typography here may look different. You can set a custom font in Design tab.</p>
					</div>
				</div>
			)}

			<div
				className={`grid place-items-center flex-1 overflow-auto ${
					!hasPreviewReviews && showMockReviews && !currentState.layout_config.custom_font
						? "pt-32"
						: !hasPreviewReviews && showMockReviews
							? "pt-28"
							: !currentState.layout_config.custom_font
								? "pt-28"
								: "pt-18"
				} pb-18`}
			>
				{!hasPreviewReviews && !isPreviewLoading && (
					<div className="max-w-3xl mx-auto px-8 sticky top-0 z-20 w-full">
						<div className={`w-full rounded-xl border p-4 mb-2 shadow-sm ${showMockReviews ? "border-blue-200 bg-blue-50" : "border-gray-200 bg-gray-50"}`}>
							<div className="flex items-center justify-between gap-4">
								<div className="flex gap-4">
									<div>
										<h2 className={`text-base font-semibold mb-1 ${showMockReviews ? "text-blue-900" : "text-gray-800"}`}>
											{showMockReviews ? "Previewing Sample Reviews" : "No Reviews Yet"}
										</h2>
										<p className={`text-sm ${showMockReviews ? "text-blue-800" : "text-gray-700"}`}>
											{showMockReviews ? "These are sample reviews and not visible to your visitors." : "Curious how your widget will look? Preview it with sample reviews."}
										</p>
									</div>
								</div>
								<Button type="button" onClick={() => setShowMockReviews((prev) => !prev)} variant="light">
									{showMockReviews ? "Hide Sample Reviews" : "Show Sample Reviews"}
								</Button>
							</div>
						</div>
					</div>
				)}

				<div className="max-w-4xl mx-auto p-8 pb-20 w-full flex justify-center *:w-full">
					{isPreviewLoading || !previewData ? (
						<div className="flex items-center justify-center min-h-[50vh]">
							<div className="w-8 h-8 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
						</div>
					) : !hasPreviewReviews && !showMockReviews ? (
						<div className="flex flex-col items-center justify-center min-h-[50vh] text-center gap-3">
							<div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
								<svg
									width="28"
									height="28"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
									className="text-gray-400"
								>
									<path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
									<path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" />
								</svg>
							</div>

							<div>
								<p className="text-sm font-medium text-gray-500">No reviews selected</p>
								<p className="text-xs text-gray-400 mt-0.5">Select reviews to preview this widget</p>
							</div>
						</div>
					) : (
						<WidgetTemplateRouter
							template={currentState.template}
							previewData={displayedPreviewData}
							layoutConfig={currentState.layout_config}
							templateDetail={templateDetail}
							aggregationConfig={currentState.aggregation_config}
							brandKit={brandKit}
							showBranding={showBranding}
							nextCursor={nextCursor}
							onLoadMore={handleLoadMore}
							isLoadingMore={isLoadingMore}
						/>
					)}
				</div>
			</div>

			<WidgetTemplateModal
				open={templateModalOpen}
				currentTemplate={currentState.template}
				currentVersion={currentState.template_version}
				onOpenChange={setTemplateModalOpen}
				onConfirm={handleTemplateConfirm}
			/>

			<SelectReviewsModal
				open={selectReviewsModalOpen}
				onOpenChange={setSelectReviewsModalOpen}
				workspaceId={workspaceId}
				initialFilters={currentState.filters}
				initialSort={currentState.sort}
				pinnedIds={currentState.pinnedReviewIds}
				tags={tags ?? []}
				onSave={handleReviewsSave}
			/>

			<SelectAggregationFiltersModal
				open={selectAggregationFiltersModalOpen}
				onOpenChange={setSelectAggregationFiltersModalOpen}
				workspaceId={workspaceId}
				initialFilters={currentState.filters}
				aggregationConfig={currentState.aggregation_config}
				tags={tags ?? []}
				onSave={handleAggregationFiltersSave}
			/>

			<SelectPinnedReviewsModal
				open={selectPinnedReviewsModalOpen}
				onOpenChange={setSelectPinnedReviewsModalOpen}
				workspaceId={workspaceId}
				wallId={widgetData?.id ?? null}
				dynamicWallFilters={reviewFilters}
				initialPinnedIds={currentState.pinnedReviewIds}
				tags={tags ?? []}
				maxPinnedReviews={maxReviewsPerWidget}
				userRole={userRole as UserRole}
				onSave={handlePinnedReviewsSave}
			/>

			<SelectSingleReviewModal
				open={selectSingleReviewModalOpen}
				onOpenChange={setSelectSingleReviewModalOpen}
				workspaceId={workspaceId}
				initialSelectedId={currentState.reviewIds.length > 0 ? currentState.reviewIds[0] : null}
				initialSelectedReview={previewData && "reviews" in previewData ? previewData.reviews?.data?.[0] || null : null}
				tags={tags ?? []}
				onSave={(reviewId, review) => handleSingleReviewSave(reviewId, review)}
			/>

			<SelectStaticReviewsModal
				open={selectStaticReviewsModalOpen}
				onOpenChange={setSelectStaticReviewsModalOpen}
				workspaceId={workspaceId}
				initialSelectedIds={currentState.reviewIds}
				tags={tags ?? []}
				maxReviews={maxReviewsPerWidget}
				userRole={userRole as UserRole}
				onSave={handleStaticReviewsSave}
			/>

			{widgetData?.slug && (
				<EmbedCodeModal open={embedCodeModalOpen} onOpenChange={setEmbedCodeModalOpen} slug={widgetData.slug} supportedDeliveryTypes={selectedApiTemplate?.supported_delivery_types ?? []} />
			)}

			<StatusChangeConfirmationModal
				open={statusChangeConfirmOpen}
				onOpenChange={setStatusChangeConfirmOpen}
				currentStatus={initialState?.status || "unpublished"}
				newStatus={currentState?.status || "unpublished"}
				onConfirm={handleSave}
				isLoading={isSaving}
			/>

			<UpgradeToGrowthModal
				open={upgradeModalOpen}
				onOpenChange={(open) => {
					setUpgradeModalOpen(open);
					if (!open) setPlanLimitError(null);
					if (!open) setWidgetCustomizationError(false);
				}}
				context={planLimitError ? "reviews_per_widget_limit" : widgetCustomizationError ? "widget_customization" : "dynamic_widgets"}
				reviewLimit={planLimitError ? maxReviewsPerWidget : undefined}
				workspaceId={workspaceId}
				userRole={userRole as UserRole}
			/>

			<div className="fixed bottom-0 left-100 right-0 z-20 px-8 py-4 bg-white border-t border-gray-200">
				<div className="max-w-6xl mx-auto px-8">
					<div className="flex items-center justify-between">
						{validationErrors && <div className="text-sm text-red-600">Please resolve validation errors before saving.</div>}
						<div className="flex items-center gap-3 ml-auto">
							{/* Status indicator */}
							{saveStatus !== "idle" && (
								<span
									className={`text-sm font-medium flex items-center gap-1.5 transition-opacity duration-200 ${
										saveStatus === "saving" ? "text-gray-500" : saveStatus === "saved" ? "text-green-600" : "text-red-600"
									}`}
								>
									{saveStatus === "saving" ? (
										<>
											<svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												/>
											</svg>
											Saving...
										</>
									) : saveStatus === "saved" ? (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
											</svg>
											Saved
										</>
									) : (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
											</svg>
											Error saving
										</>
									)}
								</span>
							)}

							<Button variant="light" onClick={handleDiscard} disabled={!isDirty || saveStatus === "saving" || saveStatus === "saved" || showMockReviews}>
								Discard
							</Button>
							{showMockReviews ? (
								<Tooltip content="Exit preview mode to save your changes.">
									<span>
										<Button variant="default" onClick={handleSaveClick} disabled>
											{saveStatus === "saving" ? "Saving..." : "Save Changes"}
										</Button>
									</span>
								</Tooltip>
							) : (
								<Button variant="default" onClick={handleSaveClick} disabled={!isDirty || isSaving || !!validationErrors || showMockReviews}>
									{saveStatus === "saving" ? "Saving..." : "Save Changes"}
								</Button>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
